import React from "react";
import * as finService from "../../services/finService";
import { fmtMoney } from "./gl/glContext";
import { DataTable, hint } from "./gl/glUi";
import { EmptyState, LoadState, PageHeader, SecondaryButton, cardStyle, formatDate, pageStyle, useLoad } from "../../components/ui";

// The Finance dashboard: cash and bank, receivables and payables by age, this period's profit,
// the VAT position and what needs attention (accounting exceptions, journals waiting).
function Tile({ label, value, sub, warn }) {
  return (
    <div style={{ ...cardStyle, padding: 16 }}>
      <div style={hint}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 700, color: warn ? "#B91C1C" : undefined }}>{value}</div>
      {sub ? <div style={hint}>{sub}</div> : null}
    </div>
  );
}

export default function FinDashboard() {
  const { data, loading, error, reload } = useLoad(() => finService.getFinDashboard(), []);
  const d = data || {};
  const ccy = d.ledger ? d.ledger.currency : "";
  return (
    <div style={pageStyle}>
      <PageHeader title="Finance Dashboard" subtitle={d.ledger ? `${d.ledger.name} · as of ${formatDate(d.as_of)}` : "Cash, receivables, payables, profit and tax at a glance"} actions={<SecondaryButton onClick={reload}>Refresh</SecondaryButton>} />
      <LoadState loading={loading} error={error} onRetry={reload}>
        {!d.ledger ? (
          <EmptyState title="No ledger yet" subtitle="Set up a ledger in Finance Setup first." />
        ) : (
          <>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 12, marginBottom: 16 }}>
              <Tile label="Cash and bank" value={`${ccy} ${fmtMoney(d.cash_and_bank)}`} sub={`Cash on hand ${fmtMoney(d.cash_on_hand)}`} />
              <Tile label="Customers owe" value={`${ccy} ${fmtMoney(d.receivables.total)}`} sub={`Over 90 days ${fmtMoney((d.receivables.buckets.find((b) => /90/.test(b.bucket) && !/61/.test(b.bucket)) || {}).amount)}`} />
              <Tile label="We owe suppliers" value={`${ccy} ${fmtMoney(d.payables.total)}`} />
              <Tile label={d.profit_and_loss ? `Net profit · ${d.profit_and_loss.period}` : "Net profit"} value={d.profit_and_loss ? `${ccy} ${fmtMoney(d.profit_and_loss.net_profit)}` : "—"} sub={d.profit_and_loss ? `Revenue ${fmtMoney(d.profit_and_loss.revenue)} · expenses ${fmtMoney(d.profit_and_loss.expenses)}` : null} />
              <Tile label="VAT" value={d.vat ? `${ccy} ${fmtMoney(d.vat.net_payable)}` : "—"} sub={d.vat ? `${d.vat.status.toLowerCase()} return ${formatDate(d.vat.period_from)} – ${formatDate(d.vat.period_to)}` : "No return prepared"} />
              <Tile label="Needs attention" value={`${d.accounting_exceptions} exceptions`} sub={`${d.journals_waiting} journals waiting`} warn={d.accounting_exceptions > 0} />
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))", gap: 16 }}>
              <div>
                <h6>Bank accounts</h6>
                <DataTable rows={d.banks} rowKey={(r) => r.code} minWidth={300} columns={[{ label: "Account", render: (r) => `${r.code} – ${r.name}` }, { label: "Balance", num: true, render: (r) => (r.balance === null ? "shared" : fmtMoney(r.balance)) }]} />
              </div>
              <div>
                <h6>Receivables by age</h6>
                <DataTable rows={d.receivables.buckets} rowKey={(r) => r.bucket} minWidth={300} columns={[{ label: "Age", render: (r) => r.bucket }, { label: "Amount", num: true, render: (r) => fmtMoney(r.amount) }]} />
              </div>
              <div>
                <h6>Payables by age</h6>
                <DataTable rows={d.payables.buckets} rowKey={(r) => r.bucket} minWidth={300} columns={[{ label: "Age", render: (r) => r.bucket }, { label: "Amount", num: true, render: (r) => fmtMoney(r.amount) }]} />
              </div>
              <div>
                <h6>Largest balances</h6>
                <DataTable
                  rows={[...d.receivables.top.map((t) => ({ ...t, side: "Customer" })), ...d.payables.top.map((t) => ({ ...t, side: "Supplier" }))]}
                  rowKey={(r) => `${r.side}-${r.name}`}
                  minWidth={300}
                  columns={[{ label: "", render: (r) => r.side }, { label: "Name", render: (r) => r.name }, { label: "Owed", num: true, render: (r) => fmtMoney(r.total) }]}
                />
              </div>
            </div>
          </>
        )}
      </LoadState>
    </div>
  );
}
